import React, { useEffect, useState, useCallback } from 'react'
import { phaserEvents, Event } from '../events/EventCenter'
import { styled } from '@mui/material/styles'

// Add LiveKit imports
import { Room, RoomEvent, ConnectionState, ConnectionQuality, Participant } from 'livekit-client'

// Import events from LiveKitVideoDisplay styles
import {
  LIVEKIT_VIDEO_STATE_CHANGED,
  LIVEKIT_AUDIO_STATE_CHANGED,
  PLAYER_PROXIMITY_CHANGED,
} from './LiveKitVideoDisplay/styles/LiveKitVideoDisplay.styles'

// Import the reconnection event from LiveKitRoomConnector
import { 
  LIVEKIT_RECONNECTION_EVENT,
  LIVEKIT_MANUAL_CONNECT_EVENT,
  LIVEKIT_MANUAL_DISCONNECT_EVENT
} from './LiveKitRoomConnector'

// Add new event for proximity area details
export const PLAYER_PROXIMITY_AREA_CHANGED = 'player-proximity-area-changed'

// Flag to determine if user is admin - implement your own admin check logic
// This could be retrieved from your auth system, player data, or elsewhere
const isAdmin = () => {
  // First check for explicit admin flag in localStorage
  if (localStorage.getItem('isAdmin') === 'true') {
    return true
  }

  // Check for admin status in user profile data
  // This is a placeholder for your actual auth system integration
  try {
    const userProfileData = localStorage.getItem('userProfile')
    if (userProfileData) {
      const profile = JSON.parse(userProfileData)
      return profile.isAdmin === true || profile.role === 'admin'
    }
  } catch (err) {
    //console.warn('Error reading user profile data:', err)
  }

  // Default to false if no admin markers found
  return false
}

// Check if we're in development environment
const isDevelopment = process.env.NODE_ENV === 'development'

const DebugBox = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: '10px',
  right: '10px',
  padding: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: '#fff',
  borderRadius: '5px',
  fontSize: '12px',
  maxWidth: '400px',
  zIndex: 9999,
  overflowWrap: 'break-word',
  maxHeight: '350px', // Increased height for more content
  overflowY: 'auto',
  display: 'none', // Hidden by default, will show when toggled
}))

// Button styling with proper typing
const ControlButton = styled('button')<{ active?: boolean }>(({ theme, active }) => ({
  margin: '5px 5px 5px 0',
  padding: '4px 8px',
  backgroundColor: active ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)',
  color: active ? '#00ff00' : '#ff0000',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: active ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)',
  },
}))

// Fix for ConnectionQuality reference
const getQualityString = (quality?: ConnectionQuality): string => {
  if (quality === undefined) return 'unknown'

  switch (quality) {
    case ConnectionQuality.Excellent:
      return 'Excellent'
    case ConnectionQuality.Good:
      return 'Good'
    case ConnectionQuality.Poor:
      return 'Poor'
    default:
      return 'Unknown'
  }
}

// Fix for the ConnectionStatus component to make theme optional
const ConnectionStatus = styled('div')<{ status: string }>(({ status }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  marginTop: '4px',
  fontWeight: 'bold',
  backgroundColor:
    status === 'connected'
      ? 'rgba(0, 255, 0, 0.3)'
      : status === 'connecting'
      ? 'rgba(255, 255, 0, 0.3)'
      : status === 'reconnecting'
      ? 'rgba(255, 165, 0, 0.3)'
      : status === 'disconnected'
      ? 'rgba(255, 0, 0, 0.3)'
      : 'rgba(128, 128, 128, 0.3)',
  color:
    status === 'connected'
      ? '#00ff00'
      : status === 'connecting'
      ? '#ffff00'
      : status === 'reconnecting'
      ? '#ffa500'
      : status === 'disconnected'
      ? '#ff0000'
      : '#ffffff',
}))

// Create an event for LiveKitRoomConnector to communicate connection status
export const LIVEKIT_CONNECTION_EVENT = 'livekit-connection-status'

// Create events for audio/video control
export const LIVEKIT_TOGGLE_AUDIO_EVENT = 'livekit-toggle-audio'
export const LIVEKIT_TOGGLE_VIDEO_EVENT = 'livekit-toggle-video'

interface LiveKitData {
  token: string
  roomName: string
  serverUrl: string
  colyseusRoomId: string
  receivedAt?: string
}

// Types for LiveKit debug display
export type LiveKitConnectionStatus = {
  state: ConnectionState
  quality?: ConnectionQuality
  participants: number
  participantsList: Array<{
    id: string
    name: string
    hasAudio: boolean
    hasVideo: boolean
    hasScreenShare?: boolean
  }>
  room?: Room
}

// Define reconnection status interface
interface ReconnectionStatus {
  isReconnecting: boolean
  attemptCount: number
  maxAttempts: number
  nextAttemptDelay: number
}

// Define proximity area interface
interface ProximityAreaInfo {
  id: string
  name: string
  players: string[] // players in this area
  distance: number // current distance from area center
  proximityOverride?: number // custom proximity radius for this area
}

// Event handler utilities that handle debug visibility
const debugEventEmitter = {
  // Allow listening for events even when debug panel isn't rendered
  init: () => {
    // This will be called once regardless of debug display visibility
    const handleLiveKitTokenForStorage = (data: LiveKitData) => {
      try {
        // Store token data for debugging purposes
        localStorage.setItem(
          'livekit_debug_last_token',
          JSON.stringify({
            ...data,
            receivedAt: new Date().toISOString(),
          })
        )
      } catch (err) {
        //console.warn('Error storing LiveKit debug data to localStorage:', err)
      }
    }

    // Set up persistent token storage for debugging
    phaserEvents.on(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitTokenForStorage)

    return () => {
      phaserEvents.off(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitTokenForStorage)
    }
  },
}

// Initialize the persistent event handlers
const cleanup = debugEventEmitter.init()

// Handle component cleanup on unmount to prevent memory leaks
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanup)
}

const LiveKitDebugDisplay: React.FC = () => {
  // Early return if not in development mode
  if (!isDevelopment) {
    return null
  }

  const [livekitData, setLivekitData] = useState<LiveKitData | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected')
  const [connectionQuality, setConnectionQuality] = useState<string>('unknown')
  const [participants, setParticipants] = useState<number>(0)
  const [lastStatusChange, setLastStatusChange] = useState<string>('')
  const [logMessages, setLogMessages] = useState<string[]>([])
  const [participantsList, setParticipantsList] = useState<
    Array<{ id: string; name: string; hasAudio: boolean; hasVideo: boolean }>
  >([])
  const [audioEnabled, setAudioEnabled] = useState<boolean>(false)
  const [videoEnabled, setVideoEnabled] = useState<boolean>(false)
  const [room, setRoom] = useState<Room | null>(null)

  // Add state for manual connection control
  const [isManuallyConnected, setIsManuallyConnected] = useState<boolean>(false)

  // Add reconnection status tracking
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false)
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0)
  const [maxReconnectAttempts, setMaxReconnectAttempts] = useState<number>(0)
  const [nextReconnectTime, setNextReconnectTime] = useState<number>(0)
  const [reconnectCountdown, setReconnectCountdown] = useState<number>(0)

  // Add proximity tracking
  const [inProximityIds, setInProximityIds] = useState<string[]>([])

  // Add proximity area tracking
  const [currentProximityArea, setCurrentProximityArea] = useState<ProximityAreaInfo | null>(null)
  const [proximityAreas, setProximityAreas] = useState<ProximityAreaInfo[]>([])

  // Set up interval for countdown timer
  useEffect(() => {
    if (!isReconnecting || nextReconnectTime <= 0) return

    // Calculate initial countdown value
    const initialCountdown = Math.ceil((nextReconnectTime - Date.now()) / 1000)
    setReconnectCountdown(initialCountdown > 0 ? initialCountdown : 0)

    const timer = setInterval(() => {
      const remaining = Math.ceil((nextReconnectTime - Date.now()) / 1000)
      setReconnectCountdown(remaining > 0 ? remaining : 0)

      // Clear interval if we've reached 0
      if (remaining <= 0) {
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [isReconnecting, nextReconnectTime])

  // Add a log message helper
  const addLogMessage = useCallback((message: string) => {
    setLogMessages((prev) => {
      const newLogs = [...prev, `${new Date().toLocaleTimeString()}: ${message}`]
      // Keep only the last 10 messages
      if (newLogs.length > 10) {
        return newLogs.slice(newLogs.length - 10)
      }
      return newLogs
    })
  }, [])

  // Toggle audio on/off
  const toggleAudio = useCallback(() => {
    const newState = !audioEnabled
    setAudioEnabled(newState)
    addLogMessage(`Toggled audio: ${newState ? 'ON' : 'OFF'}`)

    // Dispatch event to notify LiveKitVideoDisplay
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_AUDIO_EVENT, {
        detail: { enabled: newState },
      })
    )
  }, [audioEnabled, addLogMessage])

  // Toggle video on/off
  const toggleVideo = useCallback(() => {
    const newState = !videoEnabled
    setVideoEnabled(newState)
    addLogMessage(`Toggled video: ${newState ? 'ON' : 'OFF'}`)

    // Dispatch event to notify LiveKitVideoDisplay
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_VIDEO_EVENT, {
        detail: { enabled: newState },
      })
    )
  }, [videoEnabled, addLogMessage])

  // Handler for manual reconnection
  const handleManualReconnect = useCallback(() => {
    addLogMessage('Manual reconnection requested')
    window.dispatchEvent(new CustomEvent('livekit-manual-reconnect'))
  }, [addLogMessage])

  // Handler for manual connection control
  const handleManualConnect = useCallback(() => {
    if (!isManuallyConnected) {
      addLogMessage('Manual connection requested - connecting to LiveKit room')
      setIsManuallyConnected(true)
      window.dispatchEvent(new CustomEvent(LIVEKIT_MANUAL_CONNECT_EVENT))
    } else {
      addLogMessage('Manual disconnection requested - disconnecting from LiveKit room')
      setIsManuallyConnected(false)
      window.dispatchEvent(new CustomEvent(LIVEKIT_MANUAL_DISCONNECT_EVENT))
    }
  }, [isManuallyConnected, addLogMessage])

  // Effect to listen for LiveKit token events
  useEffect(() => {
    const handleLiveKitToken = (data: LiveKitData) => {
      //console.log('LiveKitDebugDisplay received token event:', data)
      setLivekitData({
        ...data,
        receivedAt: new Date().toISOString(),
      })

      addLogMessage(`Received token for room ${data.roomName}`)

      // Make debug box visible when we receive a token
      const debugBox = document.getElementById('livekit-debug-box')
      if (debugBox) {
        debugBox.style.display = 'block'
      }
    }

    // Listen for connection status updates from LiveKitRoomConnector
    const handleConnectionStatus = (event: CustomEvent<LiveKitConnectionStatus>) => {
      const status = event.detail
      //console.log('LiveKitDebugDisplay received connection status:', status)

      setConnectionStatus(status.state)
      if (status.quality) {
        setConnectionQuality(getQualityString(status.quality))
      }
      setParticipants(status.participants)
      if (status.participantsList) {
        setParticipantsList(status.participantsList)
      }
      setLastStatusChange(new Date().toISOString())

      // Capture room instance
      if (status.room) {
        setRoom(status.room)
      }

      // Update manual connection state based on actual connection status
      if (status.state === 'connected') {
        setIsManuallyConnected(true)
      } else if (status.state === 'disconnected') {
        setIsManuallyConnected(false)
      }

      // Add connection status to logs
      addLogMessage(`Connection status changed to: ${status.state}`)

      // Make debug box visible when connection status changes
      const debugBox = document.getElementById('livekit-debug-box')
      if (debugBox) {
        debugBox.style.display = 'block'
      }
    }

    // Listen for proximity status updates
    const handleProximityChanged = (event: CustomEvent<{ inProximityIds: string[] }>) => {
      const { inProximityIds: newProximityIds } = event.detail
      //console.log('LiveKitDebugDisplay received proximity update:', newProximityIds)

      // Find added and removed players
      const added = newProximityIds.filter((id) => !inProximityIds.includes(id))
      const removed = inProximityIds.filter((id) => !newProximityIds.includes(id))

      if (added.length > 0 || removed.length > 0) {
        if (added.length > 0) {
          addLogMessage(`Players in proximity: ${added.join(', ')}`)
        }
        if (removed.length > 0) {
          addLogMessage(`Players out of proximity: ${removed.join(', ')}`)
        }
      }

      setInProximityIds(newProximityIds)
    }

    // Listen for reconnection status updates
    const handleReconnectionStatus = (event: CustomEvent<ReconnectionStatus>) => {
      const status = event.detail
      //console.log('LiveKitDebugDisplay received reconnection status:', status)

      setIsReconnecting(status.isReconnecting)
      setReconnectAttempt(status.attemptCount)
      setMaxReconnectAttempts(status.maxAttempts)

      // Calculate next reconnect time if reconnecting
      if (status.isReconnecting && status.nextAttemptDelay > 0) {
        const nextTime = Date.now() + status.nextAttemptDelay
        setNextReconnectTime(nextTime)
        addLogMessage(
          `Reconnecting: attempt ${status.attemptCount}/${status.maxAttempts} in ${Math.round(
            status.nextAttemptDelay / 1000
          )}s`
        )
      } else if (!status.isReconnecting) {
        // Reset reconnection status when no longer reconnecting
        setNextReconnectTime(0)
        if (status.attemptCount > 0) {
          addLogMessage(
            `Reconnection ${status.attemptCount > status.maxAttempts ? 'failed' : 'successful'}`
          )
        }
      }
    }

    // Listen for audio state changes from LiveKitVideoDisplay
    const handleAudioStateChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      const enabled = event.detail.enabled
      //console.log('LiveKitDebugDisplay received audio state change:', enabled)
      setAudioEnabled(enabled)
      addLogMessage(`Audio state changed to: ${enabled ? 'ON' : 'OFF'}`)
    }

    // Listen for video state changes from LiveKitVideoDisplay
    const handleVideoStateChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      const enabled = event.detail.enabled
      //console.log('LiveKitDebugDisplay received video state change:', enabled)
      setVideoEnabled(enabled)
      addLogMessage(`Video state changed to: ${enabled ? 'ON' : 'OFF'}`)
    }

    // Add listener for proximity area changes
    const handleProximityAreaChanged = (
      event: CustomEvent<{
        currentArea: ProximityAreaInfo | null
        allAreas: ProximityAreaInfo[]
      }>
    ) => {
      const { currentArea, allAreas } = event.detail
      //console.log('LiveKitDebugDisplay received proximity area update:', currentArea, allAreas)

      setCurrentProximityArea(currentArea)
      setProximityAreas(allAreas)

      if (currentArea) {
        addLogMessage(`Entered proximity area: ${currentArea.name} (${currentArea.id})`)
      }
    }

    // Register event listeners
    phaserEvents.on(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitToken)
    window.addEventListener(LIVEKIT_CONNECTION_EVENT, handleConnectionStatus as EventListener)
    window.addEventListener(LIVEKIT_RECONNECTION_EVENT, handleReconnectionStatus as EventListener)
    window.addEventListener(LIVEKIT_AUDIO_STATE_CHANGED, handleAudioStateChanged as EventListener)
    window.addEventListener(LIVEKIT_VIDEO_STATE_CHANGED, handleVideoStateChanged as EventListener)
    window.addEventListener(PLAYER_PROXIMITY_CHANGED, handleProximityChanged as EventListener)
    window.addEventListener(
      PLAYER_PROXIMITY_AREA_CHANGED,
      handleProximityAreaChanged as EventListener
    )

    // Check if we have debug data in localStorage
    try {
      const storedData = localStorage.getItem('livekit_token_debug')
      if (storedData) {
        const parsedData = JSON.parse(storedData)
        //console.log('Found stored LiveKit debug data:', parsedData)
        addLogMessage(`Found stored data for room ${parsedData.roomName}`)
      }
    } catch (err) {
      //console.warn('Error reading LiveKit debug data from localStorage:', err)
    }

    // Cleanup event listeners on unmount
    return () => {
      phaserEvents.off(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitToken)
      window.removeEventListener(LIVEKIT_CONNECTION_EVENT, handleConnectionStatus as EventListener)
      window.removeEventListener(
        LIVEKIT_RECONNECTION_EVENT,
        handleReconnectionStatus as EventListener
      )
      window.removeEventListener(
        LIVEKIT_AUDIO_STATE_CHANGED,
        handleAudioStateChanged as EventListener
      )
      window.removeEventListener(
        LIVEKIT_VIDEO_STATE_CHANGED,
        handleVideoStateChanged as EventListener
      )
      window.removeEventListener(PLAYER_PROXIMITY_CHANGED, handleProximityChanged as EventListener)
      window.removeEventListener(
        PLAYER_PROXIMITY_AREA_CHANGED,
        handleProximityAreaChanged as EventListener
      )
    }
  }, [addLogMessage, inProximityIds])

  // Modify the press 'L' key to toggle display
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only allow toggling in development mode
      if (isDevelopment && (e.key === 'l' || e.key === 'L')) {
        const debugBox = document.getElementById('livekit-debug-box')
        if (debugBox) {
          debugBox.style.display = debugBox.style.display === 'none' ? 'block' : 'none'
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Add a function to check if a participant is in proximity
  const isInProximity = (participantId: string) => {
    return inProximityIds.includes(participantId)
  }

  // Effect to automatically show debug box in development environment
  useEffect(() => {
    if (isDevelopment) {
      // In dev mode, show debug box automatically
      const debugBox = document.getElementById('livekit-debug-box')
      if (debugBox) {
        // Short delay to ensure DOM is ready
        setTimeout(() => {
          debugBox.style.display = 'block'
          addLogMessage('Debug display shown automatically in development mode')
        }, 1000)
      }
    }
  }, [isDevelopment, addLogMessage])

  return (
    <DebugBox id="livekit-debug-box">
      <h4>LiveKit Debug (press L to toggle) - {isDevelopment ? 'DEV' : 'ADMIN'}</h4>
      <div style={{ 
        fontSize: '10px', 
        color: '#ffa500', 
        marginBottom: '8px',
        fontWeight: 'bold',
        background: 'rgba(255, 165, 0, 0.1)',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid rgba(255, 165, 0, 0.3)'
      }}>
        🔧 Debug Mode: Auto-connection disabled to save bandwidth
      </div>
      <ConnectionStatus status={isReconnecting ? 'reconnecting' : connectionStatus}>
        Status: {isReconnecting ? 'Reconnecting' : connectionStatus}{' '}
        {participants > 0 && `(${participants} participants)`}
        {isReconnecting && (
          <span style={{ marginLeft: '5px' }}>
            [Attempt {reconnectAttempt}/{maxReconnectAttempts}, {reconnectCountdown}s]
          </span>
        )}
      </ConnectionStatus>

      {/* Connection control - only show in debug mode */}
      <div style={{ marginTop: '10px' }}>
        <ControlButton 
          active={isManuallyConnected} 
          onClick={handleManualConnect}
          style={{ 
            backgroundColor: isManuallyConnected ? '#4caf50' : '#f44336',
            marginRight: '8px'
          }}
        >
          {isManuallyConnected ? 'Disconnect' : 'Connect'}
        </ControlButton>
        {livekitData && !isManuallyConnected && (
          <span style={{ 
            fontSize: '11px', 
            color: '#ffa500',
            marginLeft: '8px',
            fontWeight: 'bold'
          }}>
            💡 Token ready - Click Connect to join room
          </span>
        )}
      </div>

      {/* Audio/Video controls - only show when connected */}
      {isManuallyConnected && (
        <div style={{ marginTop: '10px' }}>
          <ControlButton active={audioEnabled} onClick={toggleAudio}>
            {audioEnabled ? 'Mic ON' : 'Mic OFF'}
          </ControlButton>
          <ControlButton active={videoEnabled} onClick={toggleVideo}>
            {videoEnabled ? 'Camera ON' : 'Camera OFF'}
          </ControlButton>
          {/* Manual reconnect button, only shown when disconnected and not already reconnecting */}
          {connectionStatus === 'disconnected' && !isReconnecting && (
            <ControlButton onClick={handleManualReconnect}>Reconnect</ControlButton>
          )}
          {/* Dummy camera controls */}
          <ControlButton
            style={{ background: '#444', color: '#fff', marginLeft: 8 }}
            onClick={() => {
              window.dispatchEvent(new CustomEvent('LIVEKIT_ADD_DUMMY_CAMERA'))
              addLogMessage('Dummy camera added')
            }}
          >
            + Dummy Camera
          </ControlButton>
          <ControlButton
            style={{ background: '#444', color: '#fff', marginLeft: 4 }}
            onClick={() => {
              window.dispatchEvent(new CustomEvent('LIVEKIT_REMOVE_DUMMY_CAMERA'))
              addLogMessage('Dummy camera removed')
            }}
          >
            - Dummy Camera
          </ControlButton>
        </div>
      )}


      {connectionQuality !== 'unknown' && (
        <div style={{ marginTop: '4px' }}>
          <strong>Quality:</strong> {connectionQuality}
        </div>
      )}

      {livekitData ? (
        <>
          <div>
            <strong>Room Name:</strong> {livekitData.roomName}
          </div>
          <div>
            <strong>Colyseus Room ID:</strong> {livekitData.colyseusRoomId}
          </div>
          <div>
            <strong>Server URL:</strong> {livekitData.serverUrl}
          </div>
          <div>
            <strong>Token:</strong> {livekitData.token.substring(0, 20)}...
          </div>
          <div>
            <strong>Received At:</strong> {livekitData.receivedAt}
          </div>
          {lastStatusChange && (
            <div>
              <strong>Last Status Change:</strong> {lastStatusChange}
            </div>
          )}
        </>
      ) : (
        <div>Waiting for LiveKit token...</div>
      )}

      {/* Participants list with proximity status */}
      {participantsList.length > 0 && (
        <div
          style={{
            marginTop: '10px',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            paddingTop: '5px',
          }}
        >
          <strong>Connected Participants:</strong>
          <ul style={{ margin: '5px 0', padding: '0 0 0 15px' }}>
            {participantsList.map((participant) => (
              <li key={participant.id} style={{ marginBottom: '3px' }}>
                {participant.name} (ID: {participant.id.substring(0, 6)}...)
                {participant.hasAudio && ' 🎤'}
                {participant.hasVideo && ' 📹'}
                {/* Add proximity indicator */}
                <span
                  style={{
                    marginLeft: '5px',
                    color: isInProximity(participant.id) ? '#00ff00' : '#ff0000',
                    fontWeight: 'bold',
                  }}
                >
                  {isInProximity(participant.id) ? '(In Proximity)' : '(Out of Range)'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add proximity area info section */}
      <div
        style={{
          marginTop: '10px',
          borderTop: '1px solid rgba(255,255,255,0.2)',
          paddingTop: '5px',
        }}
      >
        <strong>Proximity Areas:</strong>

        {currentProximityArea ? (
          <div style={{ margin: '5px 0' }}>
            <div style={{ color: '#00ff00', fontWeight: 'bold' }}>
              Current Area: {currentProximityArea.name} (ID: {currentProximityArea.id})
            </div>
            <div>
              Distance: {currentProximityArea.distance.toFixed(1)} px
              {currentProximityArea.proximityOverride !== undefined && (
                <span style={{ marginLeft: '5px' }}>
                  (Radius:{' '}
                  {currentProximityArea.proximityOverride === 0
                    ? 'Unlimited'
                    : `${currentProximityArea.proximityOverride} px`}
                  )
                </span>
              )}
            </div>
            {currentProximityArea.players.length > 0 && (
              <div>
                Players in this area: {currentProximityArea.players.length}
                <ul style={{ margin: '2px 0', padding: '0 0 0 15px' }}>
                  {currentProximityArea.players.map((playerId) => (
                    <li key={playerId} style={{ fontSize: '11px' }}>
                      {playerId}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div style={{ margin: '5px 0', color: '#ff6000' }}>Not in any proximity area</div>
        )}

        {/* List all proximity areas */}
        {proximityAreas.length > 0 && proximityAreas.length > (currentProximityArea ? 1 : 0) && (
          <div style={{ marginTop: '5px' }}>
            <strong>Other Areas:</strong>
            <ul style={{ margin: '2px 0', padding: '0 0 0 15px' }}>
              {proximityAreas
                .filter((area) => !currentProximityArea || area.id !== currentProximityArea.id)
                .map((area) => (
                  <li key={area.id} style={{ fontSize: '11px', marginBottom: '3px' }}>
                    {area.name} - {area.players.length} players
                    {area.proximityOverride !== undefined && (
                      <span style={{ marginLeft: '5px', fontSize: '10px', opacity: 0.8 }}>
                        (Radius:{' '}
                        {area.proximityOverride === 0
                          ? 'Unlimited'
                          : `${area.proximityOverride} px`}
                        )
                      </span>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add proximity info section */}
      {inProximityIds.length > 0 && (
        <div
          style={{
            marginTop: '10px',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            paddingTop: '5px',
          }}
        >
          <strong>Proximity Status:</strong>
          <div style={{ margin: '5px 0' }}>
            <span style={{ color: '#00ff00' }}>
              {inProximityIds.length} players in proximity range
            </span>
          </div>
        </div>
      )}

      {/* Add log messages */}
      <div
        style={{
          marginTop: '10px',
          borderTop: '1px solid rgba(255,255,255,0.2)',
          paddingTop: '5px',
        }}
      >
        <strong>Event Log:</strong>
        <ul style={{ margin: '5px 0', padding: '0 0 0 15px' }}>
          {logMessages.map((msg, i) => (
            <li key={i} style={{ fontSize: '10px', marginBottom: '3px' }}>
              {msg}
            </li>
          ))}
        </ul>
      </div>
    </DebugBox>
  )
}

// Add a conditional export that always returns null in production for non-admins
export default LiveKitDebugDisplay
